import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import Swal, { SweetAlertIcon, SweetAlertPosition } from 'sweetalert2';

export const GridPersistRequests = [
  'filtering',
  'sorting',
  'columnstate',
  'searching',
  'reorder',
  'paging',
  'resize',
  'resizing',
  'resizeStop',
];

@Injectable()
export class CommonService {
  private isCallingApiSubject = new BehaviorSubject<boolean>(false);
  public isCallingApi$ = this.isCallingApiSubject.asObservable();

  setIsCallingApi(isCallingApi: boolean): void {
    this.isCallingApiSubject.next(isCallingApi);
  }

  showNotification(
    type: SweetAlertIcon,
    message: string,
    position: SweetAlertPosition = 'center',
    timer: number = 1850,
  ) {
    return Swal.fire({
      icon: type,
      html: message,
      toast: true,
      timer: timer,
      position,
      showConfirmButton: false,
      showCloseButton: true,
    });
  }

  createdId(
    response: unknown,
    keys: string[],
    fallbackId?: unknown,
  ): string {
    return this.readCreatedId(response, keys) || this.asId(fallbackId);
  }

  /** Prefix a success toast with the grid id. Uses the API body, then a known id. */
  withCreatedId(
    response: unknown,
    message: string,
    keys: string[],
    fallbackId?: unknown,
  ): string {
    const id = this.createdId(response, keys, fallbackId);
    return id ? `${id}: ${message}` : message;
  }

  private asId(value: unknown): string {
    if (value == null || typeof value === 'object') return '';
    return String(value).trim();
  }

  private readCreatedId(response: unknown, keys: string[]): string {
    if (response == null) return '';
    if (typeof response === 'string' || typeof response === 'number') {
      const text = String(response).trim();
      if (!text || /^(ok|success|true|created)$/i.test(text)) return '';
      return text;
    }
    if (Array.isArray(response)) {
      return this.readCreatedId(response[0], keys);
    }
    if (typeof response !== 'object') return '';

    const record = response as Record<string, unknown>;
    const byKey = new Map(
      Object.entries(record).map(([key, value]) => [key.toLowerCase(), value]),
    );
    for (const key of keys) {
      const value = byKey.get(key.toLowerCase());
      if (value != null && typeof value !== 'object' && String(value).trim()) {
        return String(value).trim();
      }
    }
    for (const nestedKey of ['value', 'data', 'result']) {
      const nested = byKey.get(nestedKey);
      if (nested) {
        const nestedId = this.readCreatedId(nested, keys);
        if (nestedId) return nestedId;
      }
    }
    return '';
  }
}
